/**
 * Iter 41.2 — Admin notification bell with dropdown.
 * Polls GET /api/admin/notifications and lets admins dismiss alerts.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Inbox, CalendarClock, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { api } from '@/lib/api';
import { adminApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import { suiteLabel } from '@/lib/crmSuites';

const POLL_INTERVAL_MS = 60_000;

const fmtTime = (iso, lang) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(lang?.startsWith('es') ? 'es-ES' : 'en-US', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return ''; }
};

export default function AdminNotificationBell() {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const isClient = (user?.role || '').startsWith('client');
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [open, setOpen] = useState(false);

  const reload = useCallback(async () => {
    if (isClient) {
      try {
        const data = await api.get('/client/notifications').then((r) => r.data);
        setItems((data || []).map((n) => ({ ...n, _client: true })));
      } catch { /* ignore */ }
      return;
    }
    try {
      const data = await api.get('/admin/notifications').then((r) => r.data);
      setItems(data || []);
    } catch { /* ignore — bell is decorative */ }
    try {
      const rr = await adminApi.listRenewalReminders();
      setRenewals(rr?.items || []);
    } catch { /* role without access (e.g. client roles) — hide silently */ }
  }, [isClient]);

  useEffect(() => {
    reload();
    const id = setInterval(reload, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [reload]);

  const dismiss = async (n) => {
    try {
      await api.post(n._client ? `/client/notifications/${n.id}/dismiss` : `/admin/notifications/${n.id}/dismiss`);
      setItems((arr) => arr.filter((x) => x.id !== n.id));
    } catch { /* ignore */ }
  };

  const openLead = (n) => {
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  const ackRenewal = async (r) => {
    try {
      await adminApi.ackRenewalReminder(r.subscription_id, r.window);
      setRenewals((arr) => arr.map((x) => (
        x.subscription_id === r.subscription_id && x.window === r.window
          ? { ...x, acknowledged: true } : x
      )));
    } catch { /* ignore */ }
  };

  const pendingRenewals = renewals.filter((r) => !r.acknowledged);
  const count = items.length + pendingRenewals.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('topbar.notifications')}
          className="h-9 w-9 relative text-muted-foreground hover:text-foreground"
          data-testid="admin-notif-bell"
        >
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-bold flex items-center justify-center"
              data-testid="admin-notif-count"
            >
              {count > 9 ? '9+' : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="admin-notif-popover">
        <div className="p-3 border-b">
          <p className="font-semibold text-sm">{t('topbar.notifications', { defaultValue: 'Notifications' })}</p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {renewals.length > 0 && (
            <div className="border-b" data-testid="renewal-reminders-section">
              <p className="px-3 pt-2.5 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <CalendarClock className="h-3.5 w-3.5" />
                {t('topbar.renewals.title', { defaultValue: 'Upcoming renewals' })}
              </p>
              <ul className="divide-y">
                {renewals.map((r) => (
                  <li key={`${r.subscription_id}-${r.window}`}
                      className={`p-3 flex items-start gap-2 transition-colors ${r.acknowledged ? 'opacity-55' : 'hover:bg-muted/30'}`}
                      data-testid={`renewal-item-${r.subscription_id}`}>
                    <button type="button" className="flex-1 min-w-0 text-left"
                            onClick={() => { navigate(`/admin/clients/${r.company_id}`); setOpen(false); }}
                            data-testid={`renewal-open-${r.subscription_id}`}>
                      <p className="text-sm font-medium leading-snug">{r.company_name || '—'}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {suiteLabel(r.suite_code)} · {r.renewal_date}
                      </p>
                      <p className={`text-xs mt-0.5 font-semibold ${r.days_remaining <= 7 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                        {r.days_remaining <= 0
                          ? t('topbar.renewals.dueToday', { defaultValue: 'Renews today' })
                          : t('topbar.renewals.daysLeft', { count: r.days_remaining, defaultValue: '{{count}} days remaining' })}
                      </p>
                    </button>
                    {!r.acknowledged && (
                      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                              onClick={() => ackRenewal(r)}
                              aria-label={t('topbar.renewals.ack', { defaultValue: 'Mark as read' })}
                              title={t('topbar.renewals.ack', { defaultValue: 'Mark as read' })}
                              data-testid={`renewal-ack-${r.subscription_id}`}>
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {count === 0 && renewals.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="admin-notif-empty">
              <Inbox className="h-6 w-6 mx-auto mb-2 opacity-40" />
              {t('topbar.notifEmpty', { defaultValue: 'No new notifications.' })}
            </div>
          )}
          {items.length > 0 && (
            <ul className="divide-y">
              {items.map((n) => (
                <li key={n.id} className="p-3 flex items-start gap-2 hover:bg-muted/30 transition-colors" data-testid={`admin-notif-item-${n.id}`}>
                  <button
                    type="button"
                    className="flex-1 min-w-0 text-left"
                    onClick={() => openLead(n)}
                  >
                    <p className="text-sm font-medium leading-snug">
                      {i18n.language?.startsWith('es') ? n.title : (n.title_en || n.title)}
                    </p>
                    {n.body && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.body}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{fmtTime(n.created_at, i18n.language)}</p>
                    {n.link && n.type === 'evo_observation_assigned' && (
                      <span className="inline-block mt-1.5 text-xs font-semibold text-primary underline underline-offset-2"
                            data-testid={`notif-evo-cta-${n.id}`}>
                        {i18n.language?.startsWith('es') ? 'Responder evaluación →' : 'Answer evaluation →'}
                      </span>
                    )}
                  </button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => dismiss(n)} aria-label="Dismiss" data-testid={`admin-notif-dismiss-${n.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
